import ArrowBack from "@mui/icons-material/ArrowBack";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Typography,
} from "@mui/material";
import { LoadingSpinner } from "@tngtech/tng-ui";
import { Link as RouterLink } from "react-router";
import { useTranslator } from "../../i18n/translator";
import { ErrorDisplay } from "../ErrorDisplay";
import { ContentText } from "./ContentText";
import { FrontpageLayout } from "./FrontpageLayout";
import "./frontpage.css";
import { useFrontpageContent } from "./useConfidentialContent";
import { useReducedMotion } from "./useReducedMotion";

export const GuidePage = () => {
  const translator = useTranslator();
  const { data, error } = useFrontpageContent();
  const reducedMotion = useReducedMotion();
  if (error)
    return (
      <Box p={2}>
        <ErrorDisplay error={error} />
      </Box>
    );
  if (!data) return <LoadingSpinner />;
  return (
    <FrontpageLayout>
      <Box className="guide-head" sx={{ mt: 0.5, mb: 2.75 }}>
        <Link
          component={RouterLink}
          className="back-link"
          to="/"
          underline="hover"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          <ArrowBack aria-hidden="true" sx={{ fontSize: 16 }} />
          {translator("guideBackLink")}
        </Link>
        <Typography
          variant="inherit"
          component="h1"
          sx={{
            mt: 1.25,
            fontSize: "clamp(28px, 3.6vw, 40px)",
            fontWeight: 600,
            lineHeight: 1.15,
          }}
        >
          {translator("guide")}
        </Typography>
      </Box>
      {data.guide.chapters.map((chapter, index) => (
        <Accordion
          key={chapter.title}
          className="chapter"
          disableGutters
          elevation={0}
          slots={{ heading: "h2" }}
          slotProps={{
            transition: {
              unmountOnExit: true,
              ...(reducedMotion ? { timeout: 0 } : {}),
            },
          }}
          sx={{
            mt: 1.75,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3.5,
            "&:first-of-type, &:last-of-type": { borderRadius: 3.5 },
            "&::before": { display: "none" },
            "&.Mui-expanded": { borderColor: "primary.light", boxShadow: 2 },
          }}
        >
          <AccordionSummary
            id={`chapter-${index}-heading`}
            aria-controls={`chapter-${index}-content`}
            expandIcon={
              <ExpandMore sx={{ fontSize: 20, color: "text.secondary" }} />
            }
            sx={{
              px: 2.5,
              py: 2,
              minHeight: 0,
              "& .MuiAccordionSummary-content": {
                m: 0,
                gap: 1.75,
                alignItems: "center",
              },
              "&.Mui-focusVisible": {
                outline: (theme) => `3px solid ${theme.palette.primary.main}`,
                outlineOffset: -3,
                borderRadius: 3.5,
              },
              "& .MuiAccordionSummary-expandIconWrapper": {
                "@media (prefers-reduced-motion: reduce)": {
                  transition: "none",
                },
              },
            }}
          >
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                flexShrink: 0,
                width: 38,
                height: 38,
                display: "grid",
                placeItems: "center",
                borderRadius: "50%",
                background: (theme) =>
                  `linear-gradient(160deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                color: "primary.contrastText",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {String(index + 1).padStart(2, "0")}
            </Box>
            <Typography
              variant="inherit"
              component="span"
              sx={{ fontSize: 16.5, fontWeight: 600, lineHeight: 1.4 }}
            >
              {chapter.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              mx: 1.5,
              pt: 0.75,
              pr: 2.75,
              pb: 2.75,
              pl: { xs: 2.75, sm: 7.5 },
              borderTop: "1px dashed",
              borderColor: "divider",
              overflowWrap: "break-word",
              "& p": { mt: 1.5, lineHeight: 1.68 },
              "& ul": { mt: 1.25, pl: 2.75 },
              "& li": { mt: 0.75, lineHeight: 1.65 },
            }}
          >
            {chapter.blocks.map((block, blockIndex) =>
              block.type === "paragraph" ? (
                <Typography variant="inherit" component="p" key={blockIndex}>
                  <ContentText content={block.content} />
                </Typography>
              ) : (
                <Box component="ul" sx={{ m: 0 }} key={blockIndex}>
                  {block.items.map((item, itemIndex) => (
                    <li key={itemIndex}>
                      <ContentText content={item} />
                    </li>
                  ))}
                </Box>
              ),
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </FrontpageLayout>
  );
};
